import { useT } from '@/i18n/provider';
import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };

  return (
    <div className={`my-4 rounded-xl border-l-4 px-4 py-3 ${styles[type]}`}>
      <span className="font-semibold text-sm">{labels[type]}:</span>{' '}
      <span className="text-sm">{children}</span>
    </div>
  );
}

function HttpMethod({ method, path }: { method: string; path: string }) {
  const methodColors: Record<string, string> = {
    GET: 'bg-success-100 dark:bg-success-500/20 text-success-700 dark:text-success-400',
    POST: 'bg-info-100 dark:bg-info-500/20 text-info-700 dark:text-info-400',
    PUT: 'bg-warning-100 dark:bg-warning-500/20 text-warning-700 dark:text-warning-400',
    DELETE: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400',
  };

  return (
    <div className="flex items-center gap-3 my-3 px-4 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
      <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${methodColors[method] || ''}`}>
        {method}
      </span>
      <code className="text-sm text-gray-700 dark:text-gray-300 font-mono">{path}</code>
    </div>
  );
}

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'authentication', label: 'Authentication' },
  { id: 'base-url', label: 'Base URL' },
  { id: 'ingest-logs', label: 'Ingest Logs' },
  { id: 'log-schema', label: 'Log Schema', level: 1 },
  { id: 'single-log', label: 'Single Log', level: 1 },
  { id: 'batch-ingest', label: 'Batch Ingest', level: 1 },
  { id: 'vector-ingest', label: 'Vector Ingest', level: 1 },
  { id: 'query-logs', label: 'Query Logs' },
  { id: 'recent-logs', label: 'Recent Logs', level: 1 },
  { id: 'lpql-search', label: 'Search with LPQL', level: 1 },
  { id: 'rate-limits', label: 'Rate Limits' },
  { id: 'quotas', label: 'Quotas' },
  { id: 'errors', label: 'Error Handling' },
  { id: 'otlp-ingest', label: 'OTLP Ingest' },
  { id: 'otlp-schema-mapping', label: 'Schema Mapping', level: 1 },
  { id: 'schema-conventions', label: 'Schema Conventions' },
  { id: 'sampling-redaction', label: 'Sampling & Redaction (Planned)' },
  { id: 'examples', label: 'Examples' },
  { id: 'curl', label: 'cURL', level: 1 },
  { id: 'python', label: 'Python', level: 1 },
  { id: 'nodejs', label: 'Node.js', level: 1 },
  { id: 'go', label: 'Go', level: 1 },
];

export function HttpApiPage() {
  useDocumentMeta({ title: 'HTTP API: LogPulse Docs', description: 'REST API reference for log ingestion, querying, and management in LogPulse.' });

  const t = useT();

  return (
    <div className="flex gap-8">
      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Page header */}
        <div className="mb-8">
          <div className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-1">
            {t('docs.nav.integrations')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            {t('docs.httpApi.title')}
          </h1>
          <P>{t('docs.httpApi.intro')}</P>
        </div>

        {/* Overview */}
        <Section id="overview" title={t('docs.httpApi.overview.title')}>
          <P>{t('docs.httpApi.overview.description')}</P>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-6">
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
              <div className="text-2xl font-bold text-info-600 dark:text-info-400 mb-1">REST</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('docs.httpApi.overview.restLabel')}</div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
              <div className="text-2xl font-bold text-success-600 dark:text-success-400 mb-1">JSON</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('docs.httpApi.overview.jsonLabel')}</div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
              <div className="text-2xl font-bold text-primary-600 dark:text-primary-400 mb-1">10 MB</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('docs.httpApi.overview.bodyLimit')}</div>
            </div>
          </div>
        </Section>

        {/* Authentication */}
        <Section id="authentication" title={t('docs.httpApi.auth.title')}>
          <P>{t('docs.httpApi.auth.description')}</P>
          <CodeBlock title="Authorization header">{`Authorization: Bearer YOUR_API_KEY`}</CodeBlock>
          <P>{t('docs.httpApi.auth.createKey')}</P>
          <Callout type="warning">{t('docs.httpApi.auth.warning')}</Callout>
        </Section>

        {/* Base URL */}
        <Section id="base-url" title={t('docs.httpApi.baseUrl.title')}>
          <CodeBlock>{`https://api.logpulse.io`}</CodeBlock>
          <P>{t('docs.httpApi.baseUrl.description')}</P>
        </Section>

        {/* Ingest Logs */}
        <Section id="ingest-logs" title={t('docs.httpApi.ingest.title')}>
          <P>{t('docs.httpApi.ingest.description')}</P>

          <SubSection id="log-schema" title={t('docs.httpApi.ingest.schema.title')}>
            <P>{t('docs.httpApi.ingest.schema.description')}</P>
            <Table
              headers={['Field', 'Type', 'Required', 'Default', 'Description']}
              rows={[
                ['event', 'string | object', 'No', '""', t('docs.httpApi.ingest.schema.event')],
                ['level', 'string', 'No', '"info"', t('docs.httpApi.ingest.schema.level')],
                ['timestamp', 'string | number', 'No', 'now', t('docs.httpApi.ingest.schema.timestamp')],
                ['index', 'string', 'No', '"main"', t('docs.httpApi.ingest.schema.index')],
                ['source', 'string', 'No', '""', t('docs.httpApi.ingest.schema.source')],
                ['sourcetype', 'string', 'No', '""', t('docs.httpApi.ingest.schema.sourcetype')],
                ['host', 'string', 'No', '""', t('docs.httpApi.ingest.schema.host')],
                ['attributes', 'Record<string, string>', 'No', '{}', t('docs.httpApi.ingest.schema.attributes')],
              ]}
            />
          </SubSection>

          <SubSection id="single-log" title={t('docs.httpApi.ingest.single.title')}>
            <HttpMethod method="POST" path="/api/v1/logs" />
            <P>{t('docs.httpApi.ingest.single.description')}</P>
            <CodeBlock title="Request body">{`{
  "event": "User login successful",
  "level": "info",
  "timestamp": "2026-03-21T10:30:00.000Z",
  "index": "production",
  "source": "auth-service",
  "sourcetype": "application",
  "host": "web-01",
  "attributes": {
    "user_id": "usr_abc123",
    "ip_address": "192.168.1.100",
    "method": "POST",
    "path": "/api/auth/login"
  }
}`}</CodeBlock>
            <CodeBlock title="Response (200)">{`{
  "data": {
    "accepted": 1,
    "rejected": 0,
    "timestamp": "2026-03-21T10:30:00.000Z",
    "quotaStatus": {
      "plan": "starter",
      "limitMB": 1024,
      "usedMB": 42.5,
      "percent": 4.15,
      "blocked": false
    }
  }
}`}</CodeBlock>
          </SubSection>

          <SubSection id="batch-ingest" title={t('docs.httpApi.ingest.batch.title')}>
            <HttpMethod method="POST" path="/api/v1/logs" />
            <P>{t('docs.httpApi.ingest.batch.description')}</P>
            <CodeBlock title="Batch request body">{`[
  {
    "event": "GET /api/users 200 12ms",
    "level": "info",
    "source": "nginx",
    "sourcetype": "access_log",
    "attributes": { "status": "200", "duration_ms": "12" }
  },
  {
    "event": "Database connection timeout",
    "level": "error",
    "source": "api-server",
    "sourcetype": "application",
    "attributes": { "db": "postgres", "timeout_ms": "5000" }
  }
]`}</CodeBlock>
            <Callout type="info">{t('docs.httpApi.ingest.batch.partial')}</Callout>
          </SubSection>

          <SubSection id="vector-ingest" title={t('docs.httpApi.ingest.vector.title')}>
            <HttpMethod method="POST" path="/api/v1/ingest/vector" />
            <P>{t('docs.httpApi.ingest.vector.description')}</P>
            <Callout type="tip">{t('docs.httpApi.ingest.vector.tip')}</Callout>
          </SubSection>
        </Section>

        {/* Query Logs */}
        <Section id="query-logs" title={t('docs.httpApi.query.title')}>
          <HttpMethod method="GET" path="/api/v1/logs" />
          <P>{t('docs.httpApi.query.description')}</P>
          <Table
            headers={['Parameter', 'Type', 'Description']}
            rows={[
              ['query', 'string', t('docs.httpApi.query.qParam')],
              ['level', 'string', t('docs.httpApi.query.levelParam')],
              ['source', 'string', t('docs.httpApi.query.sourceParam')],
              ['sourcetype', 'string', t('docs.httpApi.query.sourcetypeParam')],
              ['from', 'string', t('docs.httpApi.query.fromParam')],
              ['to', 'string', t('docs.httpApi.query.toParam')],
              ['limit', 'number', t('docs.httpApi.query.limitParam')],
              ['offset', 'number', t('docs.httpApi.query.offsetParam')],
            ]}
          />
          <CodeBlock title="Example request">{`GET /api/v1/logs?query=timeout&level=error&source=api-server&from=2026-03-20T00:00:00Z&limit=100`}</CodeBlock>

          <SubSection id="recent-logs" title={t('docs.httpApi.query.recent.title')}>
            <HttpMethod method="GET" path="/api/v1/logs/recent" />
            <P>{t('docs.httpApi.query.recent.description')}</P>
          </SubSection>

          <SubSection id="lpql-search" title="Search with LPQL">
            <HttpMethod method="POST" path="/api/v1/query" />
            <P>
              Run a full LPQL query and get the results as JSON. This is the same endpoint the
              search page in the product uses, and it accepts a personal access token, so
              scripts, scheduled jobs and CI can search logs without a browser session.
            </P>
            <Table
              headers={['Field', 'Type', 'Description']}
              rows={[
                ['query', 'string', 'The LPQL query, for example index=main level=error | head 20'],
                ['from', 'string', 'Start of the time range. Relative (now-60m) or ISO-8601.'],
                ['to', 'string', 'End of the time range. Defaults to now.'],
                ['limit', 'number', 'Maximum rows returned. Defaults to 100.'],
              ]}
            />
            <CodeBlock title="Example request">{`curl -X POST https://api.logpulse.io/api/v1/query \\
  -H "Authorization: Bearer lpat_your_token_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "query": "index=main level=error | head 20",
    "from": "now-60m",
    "to": "now"
  }'`}</CodeBlock>
            <P>
              The response carries the rows, the total count and a server-side timing breakdown.
              The <code>timings</code> object is measured inside the API and excludes your network,
              which makes it the number to use when you are monitoring query latency yourself.
            </P>
            <CodeBlock title="Example response">{`{
  "data": {
    "results": [ { "timestamp": "2026-08-17T19:04:11.220Z", "level": "error", "...": "..." } ],
    "columns": ["timestamp", "level", "message"],
    "totalCount": 20,
    "took": 58,
    "timings": {
      "parseMs": 1,
      "permissionsMs": 2,
      "lookupsMs": 8,
      "compileMs": 1,
      "queryMs": 41,
      "fetchMs": 5,
      "totalMs": 58
    },
    "query": "index=main level=error | head 20"
  }
}`}</CodeBlock>

            <Callout type="info">
              <strong>Token scope and visibility.</strong> The token needs the{' '}
              <code>logs:read</code> scope. It acts with the authority of the user who created
              it, so it sees the same data that user sees in the product, and no more. Roles are
              re-read on every request: change someone&apos;s role and their tokens follow
              immediately. Every search made with a token is written to the <code>_audit</code>{' '}
              index with the token id, so you can tell which credential ran which query.
            </Callout>

            <Callout type="tip">
              Because a token carries its owner&apos;s access, create it from an account with
              only the access the job needs rather than from an owner account. Set an expiry, and
              revoke the token when the job is retired.
            </Callout>

            <Callout type="warning">
              This endpoint is read-only for tokens. LPQL queries ending in{' '}
              <code>| outputlookup</code> write a dataset, and those are rejected with{' '}
              <code>PAT_READ_ONLY</code> when a token authenticates. Use a signed-in session for
              queries that write.
            </Callout>
          </SubSection>
        </Section>

        {/* Rate Limits */}
        <Section id="rate-limits" title={t('docs.httpApi.rateLimits.title')}>
          <P>{t('docs.httpApi.rateLimits.description')}</P>
          <Table
            headers={['Endpoint', 'Limit', 'Window']}
            rows={[
              ['/api/v1/logs (POST)', '10,000', t('docs.httpApi.rateLimits.perMinute')],
              ['/api/v1/ingest/vector', '10,000', t('docs.httpApi.rateLimits.perMinute')],
              ['/api/v1/logs (GET)', '100', t('docs.httpApi.rateLimits.perMinute')],
            ]}
          />
          <P>{t('docs.httpApi.rateLimits.headers')}</P>
          <CodeBlock title="Rate limit response headers">{`X-RateLimit-Limit: 10000
X-RateLimit-Remaining: 9542
X-RateLimit-Reset: 1711018860`}</CodeBlock>
          <Callout type="warning">{t('docs.httpApi.rateLimits.exceeded')}</Callout>
        </Section>

        {/* Quotas */}
        <Section id="quotas" title={t('docs.httpApi.quotas.title')}>
          <P>{t('docs.httpApi.quotas.description')}</P>
          <Table
            headers={['Threshold', 'Behavior']}
            rows={[
              ['< 80%', t('docs.httpApi.quotas.normal')],
              ['80–100%', t('docs.httpApi.quotas.warning')],
              ['100%', t('docs.httpApi.quotas.blocked')],
            ]}
          />
          <P>{t('docs.httpApi.quotas.responseNote')}</P>
        </Section>

        {/* Error Handling */}
        <Section id="errors" title={t('docs.httpApi.errors.title')}>
          <P>{t('docs.httpApi.errors.description')}</P>
          <Table
            headers={['Status Code', 'Meaning', 'Action']}
            rows={[
              ['400', t('docs.httpApi.errors.400'), t('docs.httpApi.errors.400fix')],
              ['401', t('docs.httpApi.errors.401'), t('docs.httpApi.errors.401fix')],
              ['413', t('docs.httpApi.errors.413'), t('docs.httpApi.errors.413fix')],
              ['429', t('docs.httpApi.errors.429'), t('docs.httpApi.errors.429fix')],
              ['500', t('docs.httpApi.errors.500'), t('docs.httpApi.errors.500fix')],
            ]}
          />
          <CodeBlock title="Error response format">{`{
  "error": "Validation failed",
  "code": "VALIDATION_ERROR",
  "message": "Invalid log entry format"
}`}</CodeBlock>
        </Section>

        {/* OTLP Ingest */}
        <Section id="otlp-ingest" title="OTLP Ingest">
          <P>
            LogPulse natively accepts OpenTelemetry logs via OTLP/HTTP. Send logs to the standard
            OTLP endpoint using either protobuf or JSON encoding. This is the recommended ingestion
            method for teams already using OpenTelemetry collectors.
          </P>
          <Callout type="info">
            <strong>Note on paths:</strong> LogPulse exposes two distinct URL prefixes. The
            REST API lives under <code>/api/v1/*</code> (used everywhere else on this page),
            while OTLP ingestion follows the OpenTelemetry standard and lives under
            <code> /v1/*</code> (<code>/v1/logs</code>, <code>/v1/traces</code>). Both accept the
            same <code>Authorization: Bearer &lt;API_KEY&gt;</code> header.
          </Callout>
          <HttpMethod method="POST" path="/v1/logs" />
          <Table
            headers={['Parameter', 'Value']}
            rows={[
              ['Endpoint', 'https://api.logpulse.io/v1/logs'],
              ['Content-Type (protobuf)', 'application/x-protobuf'],
              ['Content-Type (JSON)', 'application/json'],
              ['Encoding', 'gzip recommended (Content-Encoding: gzip)'],
              ['Authentication', 'Bearer token via Authorization header'],
              ['Rate limit', '10,000 requests/min per organization'],
            ]}
          />
          <Callout type="tip">
            Use the OpenTelemetry Collector as a local aggregator to batch, compress, and forward
            logs to LogPulse. See the Kubernetes integration docs for a complete Helm chart
            setup with the OTel Collector DaemonSet.
          </Callout>

          <SubSection id="otlp-schema-mapping" title="Schema Mapping">
            <P>
              OTLP log records are mapped to LogPulse fields as follows:
            </P>
            <Table
              headers={['OTLP Field', 'LogPulse Field', 'Notes']}
              rows={[
                ['Body', 'event', 'Log message body (string or structured)'],
                ['SeverityText / SeverityNumber', 'level', 'Mapped to info/warn/error/debug/trace'],
                ['TimeUnixNano', 'timestamp', 'Nanosecond precision preserved'],
                ['Resource attributes', 'attributes.*', 'Merged into attributes with resource. prefix'],
                ['Scope attributes', 'attributes.*', 'Merged into attributes with scope. prefix'],
                ['Log record attributes', 'attributes.*', 'Stored directly as attributes'],
                ['Resource service.name', 'source', 'Used as the log source identifier'],
              ]}
            />
          </SubSection>
        </Section>

        {/* Schema Conventions */}
        <Section id="schema-conventions" title="Schema Conventions">
          <P>
            Following consistent naming conventions for attributes improves query performance and
            makes your logs easier to search. LogPulse recommends alignment with OpenTelemetry
            semantic conventions.
          </P>
          <Table
            headers={['Convention', 'Example', 'Description']}
            rows={[
              ['Dot-separated namespaces', 'k8s.pod.name, http.method', 'Group related attributes under a common prefix'],
              ['Lowercase snake_case', 'error_type, user_id', 'Consistent casing for attribute keys'],
              ['Reserved prefixes', 'k8s.*, cloud.*, host.*, service.*', 'Aligned with OTel semantic conventions; used for built-in filters'],
            ]}
          />
          <Callout type="info">
            Individual log events should not exceed 1 MB. Events larger than 1 MB are rejected
            with HTTP 413. The maximum request body size is 10 MB (can contain multiple events).
            See Platform Limits for all documented limits.
          </Callout>
        </Section>

        {/* Sampling & Redaction */}
        <Section id="sampling-redaction" title="Sampling & Redaction (Planned)">
          <P>
            Server-side sampling and PII redaction are planned features that will allow you to
            control log volume and protect sensitive data at the ingestion layer.
          </P>
          <Table
            headers={['Feature', 'Status', 'Description']}
            rows={[
              ['Server-side sampling', 'Planned', 'Define rules to keep only a percentage of matching logs (e.g., keep 10% of debug logs)'],
              ['PII redaction', 'Planned', 'Regex-based field masking applied at ingest time (e.g., mask email addresses, credit card numbers)'],
              ['Attribute filtering', 'Planned', 'Drop specific attributes before storage to reduce cardinality and storage costs'],
            ]}
          />
          <P>
            In the meantime, you can achieve similar results using Visual Pipelines. The
            redactMask node supports regex-based PII masking, and condition nodes can filter
            or sample logs before they reach storage.
          </P>
        </Section>

        {/* Examples */}
        <Section id="examples" title={t('docs.httpApi.examples.title')}>
          <P>{t('docs.httpApi.examples.description')}</P>

          <SubSection id="curl" title="cURL">
            <CodeBlock title="Send a single log">{`curl -X POST https://api.logpulse.io/api/v1/logs \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "event": "Payment processed successfully",
    "level": "info",
    "source": "payment-service",
    "attributes": {
      "amount": "49.99",
      "currency": "EUR",
      "order_id": "ord_xyz789"
    }
  }'`}</CodeBlock>
          </SubSection>

          <SubSection id="python" title="Python">
            <CodeBlock title="python: requests">{`import requests

API_KEY = "YOUR_API_KEY"
BASE_URL = "https://api.logpulse.io"

# Send a batch of logs
logs = [
    {
        "event": "User signed up",
        "level": "info",
        "source": "auth-service",
        "attributes": {"user_id": "usr_001", "plan": "growth"}
    },
    {
        "event": "Welcome email sent",
        "level": "info",
        "source": "email-service",
        "attributes": {"user_id": "usr_001", "template": "welcome"}
    }
]

response = requests.post(
    f"{BASE_URL}/api/v1/logs",
    json=logs,
    headers={"Authorization": f"Bearer {API_KEY}"}
)

print(response.json())
# {"data": {"accepted": 2, "rejected": 0, "timestamp": "...", "quotaStatus": {...}}}`}</CodeBlock>
          </SubSection>

          <SubSection id="nodejs" title="Node.js">
            <CodeBlock title="node.js: fetch">{`const API_KEY = process.env.LOGPULSE_API_KEY;

async function sendLog(event, level = 'info', attributes = {}) {
  const response = await fetch('https://api.logpulse.io/api/v1/logs', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': \`Bearer \${API_KEY}\`,
    },
    body: JSON.stringify({
      event,
      level,
      source: 'my-node-app',
      sourcetype: 'application',
      attributes,
    }),
  });

  if (!response.ok) {
    throw new Error(\`LogPulse API error: \${response.status}\`);
  }

  return response.json();
}

// Usage
await sendLog('Order created', 'info', { order_id: 'ord_123' }); // attributes`}</CodeBlock>
          </SubSection>

          <SubSection id="go" title="Go">
            <CodeBlock title="go">{`package main

import (
    "bytes"
    "encoding/json"
    "fmt"
    "net/http"
    "os"
)

type LogEntry struct {
    Event      string            \`json:"event"\`
    Level      string            \`json:"level,omitempty"\`
    Source     string            \`json:"source,omitempty"\`
    Sourcetype string           \`json:"sourcetype,omitempty"\`
    Attributes map[string]string \`json:"attributes,omitempty"\`
}

func SendLog(entry LogEntry) error {
    body, err := json.Marshal(entry)
    if err != nil {
        return err
    }

    req, err := http.NewRequest("POST",
        "https://api.logpulse.io/api/v1/logs",
        bytes.NewBuffer(body))
    if err != nil {
        return err
    }

    req.Header.Set("Content-Type", "application/json")
    req.Header.Set("Authorization",
        "Bearer "+os.Getenv("LOGPULSE_API_KEY"))

    resp, err := http.DefaultClient.Do(req)
    if err != nil {
        return err
    }
    defer resp.Body.Close()

    if resp.StatusCode != http.StatusOK {
        return fmt.Errorf("unexpected status: %d", resp.StatusCode)
    }
    return nil
}`}</CodeBlock>
          </SubSection>
        </Section>
      </div>

      <TableOfContents items={tocItems} />
    </div>
  );
}
